import { Link } from "react-router-dom";
import type { Article } from "@/types/news";

interface RankingBoardProps {
  ranked: Article[];
  picks: Article[];
}

export default function RankingBoard({ ranked, picks }: RankingBoardProps) {
  return (
    <section className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-12" aria-labelledby="ranking-heading">
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <div className="mb-4 flex items-center justify-between border-b border-background-200 pb-3">
            <h2 id="ranking-heading" className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="flex h-5 w-5 items-center justify-center text-primary-500">
                <i className="ri-bar-chart-2-line text-lg" aria-hidden="true"></i>
              </span>
              本周热榜
            </h2>
            <span className="font-label text-xs text-secondary-500">按阅读量</span>
          </div>
          <ol className="flex flex-col">
            {ranked.map((article, index) => (
              <li key={article.id} className="border-b border-background-200 last:border-b-0">
                <Link to={`/article/${article.id}`} className="group flex items-center gap-4 py-3.5">
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded font-heading text-sm font-bold ${
                      index < 3
                        ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                        : "bg-background-200 text-secondary-700"
                    }`}
                  >
                    {index + 1}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-1 font-body text-sm text-foreground-800 transition-colors group-hover:text-primary-600">
                      {article.title}
                    </span>
                    <span className="mt-1 block font-label text-xs text-secondary-500">
                      {article.channelName} · {(article.views / 10000).toFixed(1)} 万阅读
                    </span>
                  </span>
                  <i className="ri-arrow-right-up-line text-secondary-400 transition-colors group-hover:text-primary-500" aria-hidden="true"></i>
                </Link>
              </li>
            ))}
          </ol>
        </div>

        <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
          <div className="mb-4 flex items-center justify-between border-b border-background-200 pb-3">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
              <span className="flex h-5 w-5 items-center justify-center text-accent-600">
                <i className="ri-award-line text-lg" aria-hidden="true"></i>
              </span>
              编辑推荐
            </h2>
            <span className="font-label text-xs text-secondary-500">人工精选</span>
          </div>
          <div className="flex flex-col gap-4">
            {picks.map((article) => (
              <article key={article.id} className="group flex gap-4">
                <Link
                  to={`/article/${article.id}`}
                  className="relative block h-20 w-28 shrink-0 overflow-hidden rounded-md"
                >
                  <img
                    src={article.cover}
                    alt={`${article.title} 配图`}
                    title={`${article.title} - 爆料官网`}
                    className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </Link>
                <div className="min-w-0 flex-1">
                  <h3 className="line-clamp-2 font-heading text-sm font-semibold leading-snug text-foreground-950">
                    <Link to={`/article/${article.id}`} className="transition-colors hover:text-primary-600">
                      {article.title}
                    </Link>
                  </h3>
                  <p className="mt-2 flex flex-wrap items-center gap-2 font-label text-xs text-secondary-500">
                    <span className="rounded bg-secondary-100 px-2 py-0.5 text-secondary-800">
                      {article.channelName}
                    </span>
                    <span>{article.readTime}</span>
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}